import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { trackPageView } from './lib/pixel';
import { trackEvent } from './lib/track';
import { AuthProvider } from './state/auth';
import { CartProvider } from './state/cart';
import { ExperimentProvider } from './state/experiment';
import { LanguageProvider, useLang } from './i18n';
import Layout from './Layout';
import Home from './pages/Home';
import CategoryPage from './pages/Category';
import TemplateDetail from './pages/TemplateDetail';
import Create from './pages/Create';
import Login from './pages/Login';
import Register from './pages/Register';
import Account from './pages/Account';
import Cart from './pages/Cart';
import Preview from './pages/Preview';
import Demo from './pages/Demo';
import Checkout from './pages/Checkout';
import CheckoutCart from './pages/CheckoutCart';
import CheckoutSuccess from './pages/CheckoutSuccess';
import OrderDetail from './pages/OrderDetail';
import FinalizeOrder from './pages/FinalizeOrder';
import UpgradeOrder from './pages/UpgradeOrder';
import Terms from './pages/Terms';
import TherapeuticApproach from './pages/TherapeuticApproach';
import HowItWorks from './pages/HowItWorks';
import SocialEvidence from './pages/SocialEvidence';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminStats from './pages/admin/AdminStats';
import AdminAddToCarts from './pages/admin/AdminAddToCarts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminTemplates from './pages/admin/AdminTemplates';
import AdminStory from './pages/admin/AdminStory';
import AdminStoryBuild from './pages/admin/AdminStoryBuild';
import SeriesWizard from './pages/admin/SeriesWizard';
import SeriesDetail from './pages/admin/SeriesDetail';
import SeriesList from './pages/admin/SeriesList';
import AdminUsers from './pages/admin/AdminUsers';
import AdminSettings from './pages/admin/AdminSettings';
import { RequireAuth, RequireAdmin, RequireStaff } from './components/Protected';
import './App.css';

function LangRedirect({ code }) {
  const { setLang } = useLang();
  useEffect(() => {
    setLang(code);
    trackEvent('lang_switch', { slug: code });
  }, [code, setLang]);
  return <Navigate to="/" replace />;
}

function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView();
    // First-party visit event (unique-visitor + funnel analytics). Page-specific
    // events (template/category views) fire from their own pages.
    trackEvent('visit');
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
      <AuthProvider>
        <ExperimentProvider>
        <CartProvider>
          <RouteTracker />
          <Routes>
            <Route path="/he" element={<LangRedirect code="he" />} />
            <Route path="/ar" element={<LangRedirect code="ar" />} />
            {/* Standalone (no site chrome) shareable watermarked-demo viewer. */}
            <Route path="/demo/:shareToken" element={<Demo />} />
            {/* Homepage showcase books: same reader, un-watermarked (the server
                decides — see routes/stories.js), with a "make one like this" CTA. */}
            <Route path="/read/:shareToken" element={<Demo />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/c/:slug" element={<CategoryPage />} />
              <Route path="/c/:slug/:sub" element={<CategoryPage />} />
              <Route path="/p/:slug" element={<TemplateDetail />} />
              <Route path="/create/:slug" element={<Create />} />
              {/* Pay-first: create a paid book slot's story from the order. */}
              <Route path="/account/orders/:id/create/:index" element={<RequireAuth><Create orderItemMode /></RequireAuth>} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/preview/:shareToken" element={<Preview />} />
              <Route path="/checkout/:shareToken" element={<Checkout />} />
              {/* Pay-first: multi-item cart checkout (no shareToken). */}
              <Route path="/checkout" element={<CheckoutCart />} />
              <Route path="/checkout-success" element={<CheckoutSuccess />} />
              <Route path="/account/orders/:id" element={<OrderDetail />} />
              <Route path="/account/orders/:id/finalize" element={<RequireAuth><FinalizeOrder /></RequireAuth>} />
              <Route path="/account/orders/:id/upgrade" element={<RequireAuth><UpgradeOrder /></RequireAuth>} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/therapeutic-approach" element={<TherapeuticApproach />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/se" element={<SocialEvidence />} />
              {/* Shared admin-area pages — admins and editors both. */}
              <Route path="/admin" element={<RequireStaff><AdminDashboard /></RequireStaff>} />
              <Route path="/admin/orders" element={<RequireStaff><AdminOrders /></RequireStaff>} />
              <Route path="/admin/stories/build" element={<RequireStaff><AdminStoryBuild /></RequireStaff>} />
              <Route path="/admin/stories/:id" element={<RequireStaff><AdminStory /></RequireStaff>} />
              {/* Create Series — spin a base story into a batch of personalized copies. */}
              <Route path="/admin/series" element={<RequireStaff><SeriesList /></RequireStaff>} />
              <Route path="/admin/series/new" element={<RequireStaff><SeriesWizard /></RequireStaff>} />
              <Route path="/admin/series/:id" element={<RequireStaff><SeriesDetail /></RequireStaff>} />
              {/* Admin-only pages — stats, templates, users, monetary settings. */}
              <Route path="/admin/stats" element={<RequireAdmin><AdminStats /></RequireAdmin>} />
              <Route path="/admin/add-to-carts" element={<RequireAdmin><AdminAddToCarts /></RequireAdmin>} />
              <Route path="/admin/templates" element={<RequireAdmin><AdminTemplates /></RequireAdmin>} />
              <Route path="/admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
              <Route path="/admin/settings" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
            </Route>
          </Routes>
        </CartProvider>
        </ExperimentProvider>
      </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
